import { VNextButton, VNextCard, VNextPill } from "@/vnext/primitives";
import type { DemoAccessRow } from "@/vnext/routes/settings/demo-ops/demoOpsRouteModel";
import {
  formatVNextDate,
  formatVNextDateTime,
} from "@/vnext/shared/formatters";

interface DemoOpsAssignmentsCardProps {
  canViewSensitiveIdentifiers: boolean;
  inactiveAssignmentsCount: number;
  isLoading: boolean;
  onToggleShowInactiveAssignments: () => void;
  rows: DemoAccessRow[];
  showInactiveAssignments: boolean;
}

export function DemoOpsAssignmentsCard({
  canViewSensitiveIdentifiers,
  inactiveAssignmentsCount,
  isLoading,
  onToggleShowInactiveAssignments,
  rows,
  showInactiveAssignments,
}: DemoOpsAssignmentsCardProps) {
  const hasInactiveAssignments = inactiveAssignmentsCount > 0;
  const subtitle = showInactiveAssignments
    ? "Showing active people plus inactive history so you can reconcile older demo users."
    : "Showing people with active demo access and their recent preview usage.";

  return (
    <VNextCard
      actions={
        hasInactiveAssignments ? (
          <VNextButton onClick={onToggleShowInactiveAssignments} tone="quiet">
            {showInactiveAssignments
              ? "Hide inactive assignments"
              : `Show inactive assignments (${inactiveAssignmentsCount})`}
          </VNextButton>
        ) : undefined
      }
      subtitle={subtitle}
      title="Demo access users"
    >
      {isLoading ? (
        <p className="demo-ops-route__empty-state">
          Loading demo ops diagnostics…
        </p>
      ) : null}

      {!isLoading && rows.length === 0 ? (
        <p className="demo-ops-route__empty-state">
          {showInactiveAssignments
            ? "No demo access assignments found."
            : hasInactiveAssignments
              ? "No active demo-access assignments found. Show inactive assignments to inspect historical rows."
              : "No users currently have demo access."}
        </p>
      ) : null}

      {!isLoading && rows.length > 0 ? (
        <div className="demo-ops-route__table-shell">
          <table className="demo-ops-route__table">
            <thead>
              <tr>
                <th scope="col">User</th>
                <th scope="col">Access</th>
                <th scope="col">
                  {canViewSensitiveIdentifiers ? "Email" : "Email (redacted)"}
                </th>
                <th scope="col">
                  {canViewSensitiveIdentifiers
                    ? "Firebase UID"
                    : "Firebase UID (redacted)"}
                </th>
                <th scope="col">Usage (min)</th>
                <th scope="col">Last seen</th>
                <th scope="col">Access updated</th>
                <th scope="col">Granted by</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.displayName}</td>
                  <td>
                    <VNextPill
                      tone={row.demoAccessEnabled ? "success" : "warning"}
                    >
                      {row.demoAccessEnabled ? "Active" : "Inactive"}
                    </VNextPill>
                  </td>
                  <td>{row.email}</td>
                  <td className="demo-ops-route__mono">{row.firebaseId}</td>
                  <td>{row.activeMinutes.toLocaleString()}</td>
                  <td>{formatVNextDate(row.lastUsageDate)}</td>
                  <td>{formatVNextDateTime(row.updatedAt)}</td>
                  <td>{row.grantedBy}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </VNextCard>
  );
}
